<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CountrySelect from '@/components/common/CountrySelect.vue';
import { apiErrorMessage } from '@/composables/equipment/useEquipmentLookups';
import { EquipmentService } from '@/services/equipment.services';
import type { ISite } from '@/types/equipment.types';
import EquipmentField from './EquipmentField.vue';
import EquipmentModal from './EquipmentModal.vue';
import EquipmentStoreSelect from './EquipmentStoreSelect.vue';

const props = defineProps<{ site?: ISite }>();
const emit = defineEmits<{ close: []; saved: [] }>();

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const editing = computed(() => !!props.site);

const form = reactive({
  storeId: props.site?.store.id ?? '',
  name: props.site?.name ?? '',
  country: props.site?.country ?? 'BE',
  addressLine1: props.site?.addressLine1 ?? '',
  addressLine2: props.site?.addressLine2 ?? '',
  city: props.site?.city ?? '',
  postalCode: props.site?.postalCode ?? '',
});
const busy = ref(false);
const error = ref('');

const blank = (value: string) => value.trim() || null;
const canSave = computed(
  () => !!form.storeId && !!form.name.trim() && !!form.country,
);

// PUT à corps complet : un champ d'adresse vidé est effacé, il n'est jamais « laissé tel quel ».
const save = async () => {
  if (!canSave.value || busy.value) return;
  busy.value = true;
  error.value = '';
  const body = {
    name: form.name.trim(),
    country: form.country,
    addressLine1: blank(form.addressLine1),
    addressLine2: blank(form.addressLine2),
    city: blank(form.city),
    postalCode: blank(form.postalCode),
  };
  try {
    if (props.site)
      await EquipmentService.updateSite(form.storeId, props.site.id, body);
    else await EquipmentService.createSite(form.storeId, body);
    await queryClient.invalidateQueries({ queryKey: ['equipment'] });
    emit('saved');
  } catch (failure) {
    error.value = apiErrorMessage(failure, t('equipment.errors.generic'));
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <EquipmentModal
    :title="editing ? t('equipment.sites.edit') : t('equipment.sites.create')"
    @close="emit('close')"
  >
    <EquipmentField :label="t('equipment.fields.store')">
      <EquipmentStoreSelect v-model="form.storeId" :disabled="editing" />
    </EquipmentField>
    <EquipmentField
      :label="t('equipment.fields.siteName')"
      :hint="t('equipment.sites.nameHint')"
    >
      <input
        v-model="form.name"
        class="eq-input"
        type="text"
        maxlength="100"
        autocomplete="off"
      />
    </EquipmentField>
    <EquipmentField :label="t('equipment.fields.country')">
      <CountrySelect v-model="form.country" />
    </EquipmentField>
    <EquipmentField
      :label="t('equipment.fields.address1')"
      :hint="t('equipment.sites.addressHint')"
    >
      <input
        v-model="form.addressLine1"
        class="eq-input"
        type="text"
        maxlength="255"
      />
    </EquipmentField>
    <EquipmentField :label="t('equipment.fields.address2')">
      <input
        v-model="form.addressLine2"
        class="eq-input"
        type="text"
        maxlength="255"
      />
    </EquipmentField>
    <div class="sfm__row">
      <EquipmentField :label="t('equipment.fields.postalCode')">
        <input
          v-model="form.postalCode"
          class="eq-input"
          type="text"
          maxlength="20"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.city')">
        <input
          v-model="form.city"
          class="eq-input"
          type="text"
          maxlength="100"
        />
      </EquipmentField>
    </div>
    <p v-if="error" class="eq-error" role="alert">{{ error }}</p>
    <template #footer>
      <button type="button" class="eq-btn" @click="emit('close')">
        {{ t('equipment.cancel') }}
      </button>
      <button
        type="button"
        class="eq-btn eq-btn--primary"
        :disabled="!canSave || busy"
        @click="save"
      >
        {{ t('equipment.save') }}
      </button>
    </template>
  </EquipmentModal>
</template>

<style scoped>
.sfm__row {
  display: grid;
  grid-template-columns: 8rem 1fr;
  gap: 0.75rem;
}
</style>
